<script setup lang="ts">
import type { SuggestionProps } from "@tiptap/suggestion";
import type { IFuseOptions } from "fuse.js";
import type { Paths } from "type-fest";
import type { SectionSuggestion } from "./types";
import Fuse from "fuse.js";
import MenuItem from "ls/components/primitives/menu/MenuItem.vue";
import MenuLabel from "ls/components/primitives/menu/MenuLabel.vue";
import { computed, markRaw, ref, toRaw, watchEffect } from "vue";
import { useDiagramSections } from "../../queries/diagram";
import { CanvasScreenshotNodeName } from "../canvas-screenshot/types";
import { useMentionSuggestionContext } from "./context";
import SectionSuggestionListItem from "./SectionSuggestionListItem.vue";
import { usePreviewDiagramSelectionObject } from "./usePreviewDiagramSelectionObject";

const props = defineProps<SuggestionProps>();
const sectionSearchOptions: IFuseOptions<SectionSuggestion> = {
  threshold: 0.4,
  ignoreFieldNorm: true,
  keys: ["name", "aliasName"] satisfies Paths<SectionSuggestion>[],
  shouldSort: true,
};

const { data: sectionsSuggestionsRef } = useDiagramSections();
const sectionsLimit = ref(4);
const showAllSections = ref(false);

const sectionSuggestions = computed<SectionSuggestion[]>(() => {
  if (!sectionsSuggestionsRef.value) return [];
  return sectionsSuggestionsRef.value.map(section => ({
    id: section.id,
    name: section.name,
    aliasName: "section",
  }));
});

const sectionSearchIndex = computed(() => {
  // optimize fuse index by marking data as raw, so vue reactivity tracking could be skipped
  const suggestions = toRaw(sectionSuggestions.value);
  const index = markRaw(new Fuse(suggestions, sectionSearchOptions));
  return index;
});

const sectionSearchResults = computed(() => {
  if (sectionSuggestions.value.length === 0) return [];
  if (!props.query) return sectionSuggestions.value;
  return sectionSearchIndex.value.search(props.query).map(x => x.item);
});

const visibleSections = computed<SectionSuggestion[]>(() => {
  if (showAllSections.value || sectionSearchResults.value.length <= sectionsLimit.value) {
    return sectionSearchResults.value;
  }
  const sectionsSlice = sectionSearchResults.value.slice(0, sectionsLimit.value);
  return [
    ...sectionsSlice,
    {
      id: "show-all-sections",
      name: "show more",
      aliasName: "section",
      hidden: true,
    },
  ];
});

function handleShowAllSections() {
  showAllSections.value = true;
  props.editor.commands.focus();
}

const {
  userSearchResults,
  setSectionSearchResults,
  selectedIndex,
  select,
  selected,
} = useMentionSuggestionContext();

watchEffect(() => {
  setSectionSearchResults(visibleSections.value);
});

const { preview: setPreview, clear: clearPreview } = usePreviewDiagramSelectionObject();

watchEffect(() => {
  if (selectedIndex.value > userSearchResults.value.length - 1) {
    const section = visibleSections.value[selectedIndex.value - userSearchResults.value.length];
    if (section) {
      setPreview(section.id);
      return;
    }
  }

  clearPreview();
});

function selectSection(sectionSuggestion: SectionSuggestion) {
  if (props.editor.schema.nodes[CanvasScreenshotNodeName]) {
    props.editor
      .chain()
      .focus()
      .deleteRange(props.range)
      .insertBelow(({ onlyIfEmpty: true }))
      .setCanvasScreenshot({
        objectId: sectionSuggestion.id,
      })
      .run();
  } else {
    props.editor
      .chain()
      .focus()
      .deleteRange(props.range)
      .setCanvasLink({
        objectId: sectionSuggestion.id,
      })
      .insertContent({
        type: "text",
        text: " ",
      })
      .run();
  }
}

selected.on(index => {
  const result = visibleSections.value[index - userSearchResults.value.length];
  if (result) {
    if (result.id === "show-all-sections") {
      showAllSections.value = true;
      return;
    }
    selectSection(result);
    clearPreview();
  }
});
</script>

<template>
  <MenuLabel v-if="sectionSearchResults.length > 0">
    Sections
  </MenuLabel>
  <template
    v-for="(result, index) in visibleSections"
    :key="result.id"
  >
    <SectionSuggestionListItem
      v-if="!result.hidden"
      :item="result"
      :data-highlighted=" selectedIndex === index + userSearchResults.length ? '' : undefined"
      @pointermove="select(index + userSearchResults.length)"
      @click="selectSection(result)"
    />
  </template>
  <MenuItem
    v-if="sectionSearchResults.length > sectionsLimit && !showAllSections"
    :data-highlighted="selectedIndex === userSearchResults.length + visibleSections.length - 1 ? '' : undefined"
    @pointermove="select(userSearchResults.length + visibleSections.length - 1)"
    @click="handleShowAllSections"
  >
    Show more&hellip;
  </MenuItem>
</template>
